import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarPlus, MessageCircle, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TagBadge } from "@/components/StatusBadge";
import { PatientFormDialog } from "@/components/PatientFormDialog";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { WhatsAppDialog } from "@/components/WhatsAppDialog";
import Odontogram from "@/components/odontogram/Odontogram";
import { DadosTab } from "@/components/patient/DadosTab";
import { AnamneseTab } from "@/components/patient/AnamneseTab";
import { HistoryTab } from "@/components/patient/HistoryTab";
import { FinanceTab } from "@/components/patient/FinanceTab";
import { useApi, useResource, errMsg, notifyRefresh } from "@/lib/api";
import { age, initials } from "@/lib/constants";

const ALERT_KEYS = { hipertensao: "Hipertensão", diabetes: "Diabetes", cardiaco: "Cardíaco", alergia: "Alergia", anticoagulante: "Anticoagulante", gestante: "Gestante", hemorragia: "Hemorragia" };

const Header = ({ p, onEdit, onAppt, onWa, onDelete }) => {
  const alerts = Object.entries(ALERT_KEYS).filter(([k]) => p.anamnese?.[k]).map(([, v]) => v);
  return (
    <div className="surface rise mb-6 flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center">
      <div className="flex flex-1 items-center gap-4">
        <span className="font-display grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-xl font-extrabold text-white shadow-[0_10px_24px_-10px_rgba(2,132,199,0.7)]">{initials(p.name)}</span>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-extrabold tracking-tight text-slate-900 lg:text-3xl" data-testid="patient-detail-name">{p.name}</h1>
          <p className="text-sm text-slate-500">{p.birth_date ? `${age(p.birth_date)} anos · ` : ""}{p.phone || "sem telefone"}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {p.ortho?.active && <TagBadge testId="patient-tag-ortho">Aparelho</TagBadge>}
            {p.return_plan?.active && <TagBadge testId="patient-tag-return">Retorno</TagBadge>}
            {alerts.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700" data-testid="patient-health-alert">
                <AlertTriangle className="h-3 w-3" /> {alerts.join(", ")}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={onWa} data-testid="patient-whatsapp-btn"><MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp</Button>
        <Button variant="outline" className="rounded-full" onClick={onAppt} data-testid="patient-new-appointment-btn"><CalendarPlus className="mr-1.5 h-4 w-4" /> Agendar</Button>
        <Button variant="outline" className="rounded-full" onClick={onEdit} data-testid="patient-edit-btn"><Pencil className="mr-1.5 h-4 w-4" /> Editar</Button>
        <Button variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50" onClick={onDelete} data-testid="patient-delete-btn"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { data: p } = useResource(`/patients/${id}`);
  const { data: treatments } = useResource(`/patients/${id}/treatments`);
  const [edit, setEdit] = useState(false);
  const [appt, setAppt] = useState(false);
  const [wa, setWa] = useState(false);

  if (!p) return <div className="h-40 animate-pulse rounded-2xl bg-sky-50" />;

  const remove = async () => {
    if (!window.confirm(`Excluir ${p.name} e todo o histórico, agenda e financeiro?`)) return;
    try { await api.delete(`/patients/${p.id}`); toast.success("Paciente excluído"); notifyRefresh(); navigate("/pacientes"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <button onClick={() => navigate("/pacientes")} className="mb-4 flex items-center text-sm font-semibold text-slate-500 hover:text-sky-700" data-testid="patient-back-btn">
        <ArrowLeft className="mr-1 h-4 w-4" /> Pacientes
      </button>
      <Header p={p} onEdit={() => setEdit(true)} onAppt={() => setAppt(true)} onWa={() => setWa(true)} onDelete={remove} />
      <Tabs defaultValue="odontograma">
        <TabsList className="mb-5 h-auto flex-wrap justify-start bg-sky-50 p-1">
          <TabsTrigger value="dados" data-testid="tab-dados">Dados</TabsTrigger>
          <TabsTrigger value="anamnese" data-testid="tab-anamnese">Anamnese</TabsTrigger>
          <TabsTrigger value="odontograma" data-testid="tab-odontograma">Odontograma</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="tab-financeiro">Financeiro</TabsTrigger>
        </TabsList>
        <TabsContent value="dados"><DadosTab p={p} /></TabsContent>
        <TabsContent value="anamnese"><AnamneseTab p={p} /></TabsContent>
        <TabsContent value="odontograma"><Odontogram patientId={p.id} initial={p.odontogram} treatments={treatments || []} /></TabsContent>
        <TabsContent value="historico"><HistoryTab p={p} treatments={treatments || []} /></TabsContent>
        <TabsContent value="financeiro"><FinanceTab p={p} /></TabsContent>
      </Tabs>
      <PatientFormDialog open={edit} onOpenChange={setEdit} patient={p} />
      <AppointmentDialog open={appt} onOpenChange={setAppt} initial={{ patient_id: p.id }} />
      <WhatsAppDialog open={wa} onOpenChange={setWa} name={p.name} phone={p.phone} template="livre" />
    </div>
  );
}
